import { z } from "zod";
import { Id } from "./common";
import { defineRoutes } from "./define";

/** Mirrors core's catalogue; the API is the source of truth, the client renders what it gets. */
export const NotificationChannel = z.enum(["telegram", "email"]);
export type NotificationChannel = z.infer<typeof NotificationChannel>;

export const NotificationKind = z.enum([
  "appointment.booked",
  "appointment.rescheduled",
  "appointment.cancelled",
  "appointment.reminder",
  "visit.follow_up",
]);
export type NotificationKind = z.infer<typeof NotificationKind>;

export const ChannelFlags = z.object({ telegram: z.boolean(), email: z.boolean() });

export const KindPolicy = z.object({
  enabled: z.boolean(),
  channels: ChannelFlags,
  /** Catalogue-defined numeric options (`leadHours` on the reminder). */
  options: z.record(z.string(), z.number()),
});
export type KindPolicy = z.infer<typeof KindPolicy>;

export const NotificationPolicy = z.object({
  /** Sent back on save for compare-and-set; a 409 means someone saved in between. */
  version: z.number().int().min(0),
  mode: z.enum(["first", "all"]),
  channelOrder: z.array(NotificationChannel),
  kinds: z.record(NotificationKind, KindPolicy),
});
export type NotificationPolicy = z.infer<typeof NotificationPolicy>;

export const ChannelAvailability = z.record(
  NotificationChannel,
  z.object({ configured: z.boolean(), reason: z.string().optional() }),
);
export type ChannelAvailability = z.infer<typeof ChannelAvailability>;

export const ChoiceOption = z.object({
  type: z.literal("choice"),
  choices: z.array(z.number()),
  default: z.number(),
});

export const KindMeta = z.object({
  kind: NotificationKind,
  audience: z.literal("patient"),
  group: z.enum(["appointments", "visits"]),
  defaultChannels: ChannelFlags,
  options: z.record(z.string(), ChoiceOption),
});
export type KindMeta = z.infer<typeof KindMeta>;

export const NotificationSettings = z.object({
  policy: NotificationPolicy,
  availability: ChannelAvailability,
  catalogue: z.array(KindMeta),
  updatedAt: z.string().nullable(),
  updatedBy: z.object({ id: Id, name: z.string() }).nullable(),
});
export type NotificationSettings = z.infer<typeof NotificationSettings>;

export const UpdateNotificationsBody = z.object({ policy: NotificationPolicy });
export type UpdateNotificationsBody = z.infer<typeof UpdateNotificationsBody>;

export const PreviewBody = z.object({
  kind: NotificationKind,
  channel: NotificationChannel,
  theme: z.enum(["light", "dark"]).optional(),
});
export type PreviewBody = z.infer<typeof PreviewBody>;

export const NotificationPreview = z.object({
  subject: z.string().nullable(),
  html: z.string().nullable(),
  text: z.string(),
  button: z.boolean(),
  /** What the preview was rendered with — the specialist is one of the clinic's own. */
  sample: z.object({ firstName: z.string(), when: z.string(), specialistName: z.string() }),
});
export type NotificationPreview = z.infer<typeof NotificationPreview>;

/** The page prefills `to` with the signed-in address; any inbox the staff member wants is fine. */
export const TestSendBody = z.object({ kind: NotificationKind, to: z.string().trim().email() });
export type TestSendBody = z.infer<typeof TestSendBody>;

export const DeliveryItem = z.object({
  id: Id,
  kind: NotificationKind,
  channel: z.enum(["telegram", "email", "none"]),
  patientId: Id,
  patientName: z.string(),
  source: z.string(),
  status: z.enum(["sent", "skipped", "failed"]),
  detail: z.string().nullable(),
  createdAt: z.string(),
});
export type DeliveryItem = z.infer<typeof DeliveryItem>;

export const notifications = defineRoutes({
  settings: {
    method: "GET",
    path: "/settings/notifications",
    summary: "The policy, what each channel can do on this deployment, and the catalogue",
    responses: { 200: NotificationSettings },
  },
  update: {
    method: "PUT",
    path: "/settings/notifications",
    summary: "Save the whole policy (compare-and-set on its version)",
    body: UpdateNotificationsBody,
    responses: { 200: NotificationPolicy },
  },
  preview: {
    method: "POST",
    path: "/settings/notifications/preview",
    summary: "Render one kind on one channel with sample data",
    body: PreviewBody,
    responses: { 200: NotificationPreview },
  },
  sendTest: {
    method: "POST",
    path: "/settings/notifications/test",
    summary: "Email the sample of one kind to the signed-in staff member",
    body: TestSendBody,
    responses: { 202: z.object({ to: z.string() }) },
  },
  deliveries: {
    method: "GET",
    path: "/settings/notifications/deliveries",
    summary: "The most recent deliveries with their outcome",
    query: z.object({ limit: z.coerce.number().int().min(1).max(100).default(20) }),
    responses: { 200: z.array(DeliveryItem) },
  },
});
